import type { ReactElement, ReactNode } from 'react';

import { Loading } from '@ui/uikit/components/icons/mono/Loading';
import { MarkCheck } from '@ui/uikit/components/icons/mono/MarkCheck';
import { MarkCheckDotted } from '@ui/uikit/components/icons/mono/MarkCheckDotted';
import { MarkX } from '@ui/uikit/components/icons/mono/MarkX';

export enum VerificationStatus {
  NOT_VERIFIED = 'NOT_VERIFIED',
  IN_PROGRESS = 'IN_PROGRESS',
  VERIFIED = 'VERIFIED',
  REJECTED = 'REJECTED',
}

type StatusConfigItem = {
  icon: ReactNode;
  title: string;
  status: VerificationStatus;
  textColor: string;
  buttonContent: ReactNode;
  buttonBgColor: string;
  disabled: boolean;
};

export const CardConfig: Record<VerificationStatus, StatusConfigItem> = {
  [VerificationStatus.IN_PROGRESS]: {
    icon: (
      <Loading className="mt-[2px] size-[16px] animate-loading-spin text-base-text-quaternary" />
    ),
    title: 'In progress',
    status: VerificationStatus.IN_PROGRESS,
    textColor: 'text-base-text-quaternary',
    buttonContent: 'Verify',
    buttonBgColor: 'msq-btn-primary',
    disabled: true,
  },
  [VerificationStatus.NOT_VERIFIED]: {
    icon: <MarkCheckDotted className="mt-[2px] size-[16px] text-base-text-quaternary" />,
    title: 'Not verified',
    status: VerificationStatus.NOT_VERIFIED,
    textColor: 'text-base-text-quaternary',
    buttonContent: 'Verify',
    buttonBgColor: 'msq-btn-primary',
    disabled: false,
  },
  [VerificationStatus.VERIFIED]: {
    icon: <MarkCheck className="mt-[2px] size-[16px] text-base-text-green" />,
    title: 'Verified',
    status: VerificationStatus.VERIFIED,
    textColor: 'text-base-text-primary',
    buttonContent: <MarkCheck className="mt-[2px] size-[16px] text-base-text-quaternary" />,
    buttonBgColor: 'bg-button-secondary-disabled-bg',
    disabled: true,
  },
  [VerificationStatus.REJECTED]: {
    icon: <MarkX className="mt-[2px] size-[16px] text-base-text-red" />,
    title: 'Rejected',
    status: VerificationStatus.REJECTED,
    textColor: 'text-base-text-primary',
    buttonContent: 'Retry',
    buttonBgColor: 'msq-btn-primary',
    disabled: false,
  },
};

const Status = ({ status }: { status: VerificationStatus }) => {
  const item = CardConfig[status];
  return (
    <div className="flex items-center gap-1.5">
      {item.icon}
      <h3 className={`text-h3-a ${item.textColor}`}>{item.title}</h3>
    </div>
  );
};

export type TVerificationCard = {
  description: string;
  status: VerificationStatus;
  icon: ReactElement;
  hidden: boolean;
  onClick?(): void;
  dataQa?: string;
};

type Props = {
  card: TVerificationCard;
};

export const VerificationCard = ({ card }: Props) => {
  const { description, status, onClick, icon, dataQa } = card;
  const item = CardConfig[status];

  return (
    <div
      className="flex flex-col py-5 px-6 border border-base-border-medium rounded-xl gap-1"
      data-qa={dataQa}
    >
      <div className="flex items-center justify-between">
        <span className="text-caption-md-a">{description}</span>
        {icon}
      </div>
      <Status status={status} />
      <button
        className={`msq-btn msq-btn-lg w-full mb-1 mt-3 ${item.buttonBgColor}`}
        onClick={onClick}
        disabled={item.disabled}
      >
        {item.buttonContent}
      </button>
    </div>
  );
};
